function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatUsdMoney(cents) {
  const safeCents = toFiniteNumber(cents) ?? 0;
  return `$${(safeCents / 100).toFixed(2)}`;
}

function formatMarketMoney(cents, { currencyCode = "USD", locale = "en-US" } = {}) {
  const safeCents = toFiniteNumber(cents) ?? 0;
  const safeCurrencyCode = String(currencyCode || "USD").toUpperCase();

  try {
    return `${new Intl.NumberFormat(locale || "en-US", {
      style: "currency",
      currency: safeCurrencyCode,
      currencyDisplay: "narrowSymbol"
    }).format(safeCents / 100)} ${safeCurrencyCode}`;
  } catch {
    return `${formatUsdMoney(safeCents)} ${safeCurrencyCode}`;
  }
}

function parseLocalizedNumber(value) {
  if (!value) return null;

  const match = String(value).match(/[-+]?\d[\d\s,.'’]*(?:[.,]\d{1,2})?/);
  if (!match) return null;

  const numberText = match[0].replace(/[\s'’]/g, "");
  const lastComma = numberText.lastIndexOf(",");
  const lastDot = numberText.lastIndexOf(".");
  const decimalIndex = Math.max(lastComma, lastDot);
  let normalized;

  if (decimalIndex >= 0 && numberText.length - decimalIndex - 1 <= 2) {
    const integerPart = numberText.slice(0, decimalIndex).replace(/[^\d-]/g, "");
    const decimalPart = numberText.slice(decimalIndex + 1).replace(/\D/g, "");
    normalized = `${integerPart}.${decimalPart}`;
  } else {
    normalized = numberText.replace(/[^\d-]/g, "");
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDisplayedMoneyToCents(text) {
  if (!text) return null;

  const dollars = parseLocalizedNumber(String(text).replace(/\s+/g, " "));
  if (dollars === null) return null;

  return Math.round(dollars * 100);
}

function clampPositiveQuantity(value) {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function discountedRollTotalCents(unitPriceCents, quantity) {
  const safeUnitPrice = toFiniteNumber(unitPriceCents) ?? 0;
  const safeQuantity = clampPositiveQuantity(quantity);
  if (safeQuantity < 3) return safeUnitPrice * safeQuantity;
  return (safeUnitPrice - Math.floor(safeUnitPrice * 0.1)) * safeQuantity;
}

function computeStickyTotalCents({
  baseUnitPriceCents,
  mainQuantity,
  addonTotalCents,
  overrideTotalCents
}) {
  const safeAddonTotal = toFiniteNumber(addonTotalCents) ?? 0;
  const safeOverride = toFiniteNumber(overrideTotalCents);

  if (safeOverride !== null) {
    return safeOverride + safeAddonTotal;
  }

  const safeBasePrice = toFiniteNumber(baseUnitPriceCents) ?? 0;
  return safeBasePrice * clampPositiveQuantity(mainQuantity) + safeAddonTotal;
}

function findRealAddButton(root) {
  const explicitSelector = root.dataset.realAddButtonSelector;
  const explicitTarget = explicitSelector ? document.querySelector(explicitSelector) : null;
  return (
    document.querySelector("product-form button[name=\"add\"]") ||
    document.querySelector("form[data-type=\"add-to-cart-form\"] button[name=\"add\"]") ||
    document.querySelector('button[name="add"][form]') ||
    explicitTarget
  );
}

function findMainForm() {
  return (
    document.querySelector("product-form form[data-type=\"add-to-cart-form\"]") ||
    document.querySelector("product-form form[action=\"/cart/add\"]") ||
    document.querySelector("form[data-type=\"add-to-cart-form\"]") ||
    document.querySelector("form[action=\"/cart/add\"]")
  );
}

// Show the bar only after the real add-to-cart button has scrolled above the
// viewport. While the button is still below the fold (first view), the bar
// stays hidden so it never covers the product title.
export function shouldShowSticky(entry) {
  if (!entry || entry.isIntersecting) return false;
  const rect = entry.boundingClientRect;
  if (!rect) return true;
  return rect.bottom <= 0;
}

export function initStickyAddToCart(root) {
  if (!root || root.dataset.initialized === "true") return root;
  if (document.documentElement.dataset.ndB2bView === "wholesale") {
    root.hidden = true;
    root.classList.add("nd-suppressed");
    document.documentElement.classList.remove("nd-sticky-atc-visible");
    document.documentElement.style.setProperty("--nd-sticky-atc-offset", "0px");
    root.dataset.initialized = "true";
    return root;
  }
  root.dataset.initialized = "true";

  const stickyButton = root.querySelector("[data-sticky-add-button]");
  const priceEl = root.querySelector("[data-sticky-price]");
  const mainPriceRoot = document.getElementById(root.dataset.mainPriceRootId) || document.querySelector('[id^="price-"]');
  let currentVariantCents = Number.parseInt(root.dataset.currentVariantCents || "0", 10) || 0;
  let addonTotalCents = 0;
  let mainPriceOverrideCents = null;

  if (!stickyButton || !priceEl) return root;

  const formatPrice = (cents) =>
    formatMarketMoney(cents, {
      currencyCode: root.dataset.currencyIso,
      locale: root.dataset.locale || document.documentElement.lang || window.Shopify?.locale
    });
  const cartDrawer = document.querySelector("cart-drawer");

  const syncFloatingOverlapState = () => {
    const stickyIsVisible =
      root.classList.contains("nd-visible") &&
      !root.classList.contains("nd-suppressed") &&
      !document.body.classList.contains("nd-cart-open");

    const stickyHeight = Math.ceil(root.getBoundingClientRect().height || 0);
    document.documentElement.classList.toggle("nd-sticky-atc-visible", stickyIsVisible);
    document.documentElement.style.setProperty(
      "--nd-sticky-atc-offset",
      stickyIsVisible ? `${stickyHeight + 10}px` : "0px"
    );
  };

  const readDisplayedUnitPriceCents = () => {
    if (!mainPriceRoot) return null;

    const saleEl = mainPriceRoot.querySelector(".price-item--sale");
    const regularEls = Array.from(mainPriceRoot.querySelectorAll(".price-item--regular"));
    const candidate = saleEl || regularEls[regularEls.length - 1] || mainPriceRoot;
    return parseDisplayedMoneyToCents(candidate.textContent);
  };

  const findMainQuantityInput = () => {
    const explicit = root.dataset.quantityInputId ? document.getElementById(root.dataset.quantityInputId) : null;
    if (explicit) return explicit;

    const form = findMainForm();
    if (!form) return null;

    const direct = form.querySelector('input[name="quantity"]');
    if (direct) return direct;

    const formId = form.getAttribute("id");
    if (!formId) return null;
    return document.querySelector(`input[name="quantity"][form="${formId}"]`);
  };

  const currentMainQuantity = () => {
    const input = findMainQuantityInput();
    return clampPositiveQuantity(input?.value || 1);
  };

  const readAddonTotalCents = () => {
    let totalCents = 0;
    const addonRoot = document.querySelector("[data-nd-fleece-addon]");
    if (addonRoot instanceof HTMLElement) {
      const selectedOption =
        addonRoot.querySelector("[data-addon-option].selected") ||
        addonRoot.querySelector(`[data-addon-option="${addonRoot.dataset.defaultAddon || "none"}"]`);
      const selectedAddon = selectedOption?.getAttribute("data-addon-option") || "none";
      if (selectedAddon === "onetime") {
        const unitCents = Number.parseInt(addonRoot.dataset.onetimeUnitCents || "0", 10) || 0;
        const qtyNode = addonRoot.querySelector('[data-addon-qty-value="onetime"]');
        totalCents += discountedRollTotalCents(
          unitCents,
          clampPositiveQuantity(qtyNode?.textContent || 1)
        );
      } else if (selectedAddon === "subscribe") {
        const unitCents = Number.parseInt(addonRoot.dataset.subscribeUnitCents || "0", 10) || 0;
        const qtyNode = addonRoot.querySelector('[data-addon-qty-value="subscribe"]');
        totalCents += unitCents * clampPositiveQuantity(qtyNode?.textContent || 1);
      }
    }

    const nfillRoot = document.querySelector("[data-nd-nfill-addon]");
    if (nfillRoot instanceof HTMLElement) {
      nfillRoot.querySelectorAll("[data-nfill-addon-item].selected").forEach((option) => {
        const unitCents = Number.parseInt(option.dataset.addonUnitCents || "0", 10) || 0;
        const qtyNode = option.querySelector("[data-addon-qty-value]");
        totalCents += unitCents * clampPositiveQuantity(qtyNode?.textContent || 1);
      });
    }

    return totalCents;
  };

  const updateStickyPrice = () => {
    const totalCents = computeStickyTotalCents({
      baseUnitPriceCents: currentVariantCents,
      mainQuantity: currentMainQuantity(),
      addonTotalCents,
      overrideTotalCents: mainPriceOverrideCents
    });
    priceEl.textContent = formatPrice(totalCents);
  };

  const syncCartDrawerState = () => {
    if (!(cartDrawer instanceof Element)) return;

    const isOpen =
      cartDrawer.classList.contains("active") ||
      cartDrawer.hasAttribute("open") ||
      cartDrawer.getAttribute("aria-hidden") === "false";

    root.classList.toggle("nd-suppressed", isOpen);
    syncFloatingOverlapState();
  };

  stickyButton.addEventListener("click", () => {
    const realButton = findRealAddButton(root);
    if (!realButton) return;

    realButton.click();
    stickyButton.textContent = root.dataset.addingLabel || "…";
    window.setTimeout(() => {
      stickyButton.textContent = root.dataset.buttonLabel || window.variantStrings?.addToCart || stickyButton.textContent;
    }, 1500);
  });

  const originalButton = findRealAddButton(root);
  if (originalButton && "IntersectionObserver" in window) {
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        root.classList.toggle("nd-visible", shouldShowSticky(entry));
        syncFloatingOverlapState();
      });
    }, { threshold: 0 });
    observer.observe(originalButton);
  }

  const titleEl = root.querySelector("[data-sticky-title]");

  const updateStickyTitle = (variant) => {
    if (!variant) return;
    if (titleEl && titleEl.dataset.shortTitle) {
      const variantTitle = variant.title;
      const hasVariants = Number(titleEl.dataset.variantCount || "2") > 1;
      titleEl.textContent =
        hasVariants && variantTitle && variantTitle !== "Default Title" && variantTitle !== titleEl.dataset.shortTitle
          ? `${titleEl.dataset.shortTitle} · ${variantTitle}`
          : titleEl.dataset.shortTitle;
    }
  };

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      if (!data?.sectionId || mainPriceRoot?.id !== `price-${data.sectionId}`) return;
      updateStickyTitle(data.variant);
    });
  }

  document.addEventListener("variant:change", (event) => {
    if (!event.detail?.variant) return;
    updateStickyTitle(event.detail.variant);
    currentVariantCents = event.detail.variant.price;
    mainPriceOverrideCents = null;
    updateStickyPrice();
  });

  document.addEventListener("nd:fleece-addon-change", (event) => {
    addonTotalCents = event.detail?.addonTotalCents || 0;
    updateStickyPrice();
  });

  document.addEventListener("nd:nfill-addon-change", (event) => {
    addonTotalCents = event.detail?.addonTotalCents || 0;
    updateStickyPrice();
  });

  document.addEventListener("nd:main-price-override", (event) => {
    const totalCents = event.detail?.totalCents;
    mainPriceOverrideCents = typeof totalCents === "number" ? totalCents : null;
    updateStickyPrice();
  });

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.getAttribute("name") === "quantity") {
      updateStickyPrice();
      return;
    }

    if (target.getAttribute("name") === "selling_plan") {
      window.setTimeout(() => {
        const displayedUnitPriceCents = readDisplayedUnitPriceCents();
        if (displayedUnitPriceCents !== null) {
          currentVariantCents = displayedUnitPriceCents;
        }
        if (mainPriceOverrideCents === null) {
          updateStickyPrice();
        }
      }, 0);
    }
  });

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("quantity-input button")) {
      window.setTimeout(updateStickyPrice, 0);
    }
  });

  if (mainPriceRoot && "MutationObserver" in window) {
    const observer = new window.MutationObserver(() => {
      if (mainPriceOverrideCents !== null) return;

      const displayedUnitPriceCents = readDisplayedUnitPriceCents();
      if (displayedUnitPriceCents !== null) {
        currentVariantCents = displayedUnitPriceCents;
        updateStickyPrice();
      }
    });

    observer.observe(mainPriceRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  if (cartDrawer && "MutationObserver" in window) {
    const drawerObserver = new window.MutationObserver(syncCartDrawerState);
    drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ["class", "open", "aria-hidden"]
    });
  }

  if ("MutationObserver" in window) {
    const overlapObserver = new window.MutationObserver(syncFloatingOverlapState);
    overlapObserver.observe(root, { attributes: true, attributeFilter: ["class"] });
    overlapObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  }

  if ("ResizeObserver" in window) {
    const overlapResizeObserver = new window.ResizeObserver(syncFloatingOverlapState);
    overlapResizeObserver.observe(root);
  }

  const initialDisplayedUnitPriceCents = readDisplayedUnitPriceCents();
  if (initialDisplayedUnitPriceCents !== null) {
    currentVariantCents = initialDisplayedUnitPriceCents;
  }
  addonTotalCents = readAddonTotalCents();
  syncCartDrawerState();
  syncFloatingOverlapState();
  updateStickyPrice();
  window.setTimeout(() => {
    const displayedUnitPriceCents = readDisplayedUnitPriceCents();
    if (displayedUnitPriceCents !== null) {
      currentVariantCents = displayedUnitPriceCents;
    }
    addonTotalCents = readAddonTotalCents();
    updateStickyPrice();
  }, 0);
  return root;
}

function autoInit() {
  document.querySelectorAll("[data-nd-sticky-atc]").forEach((root) => {
    initStickyAddToCart(root);
  });
}

if (typeof window !== "undefined") {
  window.NDStorefront = window.NDStorefront || {};
  window.NDStorefront.initStickyAddToCart = initStickyAddToCart;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit, { once: true });
  } else {
    autoInit();
  }
}
